import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiEdit, 
  FiTrash2, 
  FiCalendar, 
  FiClock, 
  FiMapPin, 
  FiUser, 
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiFileText,
  FiPlus
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './VisitaDetalhes.css';

const VisitaDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [visita, setVisita] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizandoPresenca, setAtualizandoPresenca] = useState(false);

  const tiposVisita = {
    'mocidade': 'Visita - Mocidade',
    'criancas': 'Visita - Crianças',
    'ajuntamento': 'Ajuntamento',
    'reuniao_familiar': 'Reunião Familiar',
    'reuniao_evangelizacao': 'Reunião Evangelização'
  };

  useEffect(() => {
    if (id) {
      carregarDados();
    }
  }, [id, carregarDados]);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar dados da visita
      const visitaResponse = await api.get(`/visitas/${id}`);
      setVisita(visitaResponse.data);
      
      // Carregar participantes
      const participantesResponse = await api.get(`/visitas/${id}/participantes`);
      setParticipantes(participantesResponse.data);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados da visita');
      navigate('/visitas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const excluirVisita = async () => {
    if (window.confirm('Tem certeza que deseja excluir esta visita? Esta ação não pode ser desfeita.')) {
      try {
        await api.delete(`/visitas/${id}`);
        toast.success('Visita excluída com sucesso!');
        navigate('/visitas');
      } catch (error) {
        console.error('Erro ao excluir visita:', error);
        toast.error('Erro ao excluir visita');
      }
    }
  };

  const togglePresenca = async (membroId, presente) => {
    try {
      setAtualizandoPresenca(true);
      
      await api.patch(`/visitas/${id}/participantes/${membroId}/presenca`, {
        presente: !presente
      });
      
      // Atualizar estado local
      setParticipantes(prev => prev.map(p => 
        p.membro_id === membroId ? { ...p, presente: !presente } : p
      ));
      
      toast.success('Presença atualizada com sucesso!');
      
    } catch (error) {
      console.error('Erro ao atualizar presença:', error);
      toast.error('Erro ao atualizar presença');
    } finally {
      setAtualizandoPresenca(false);
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatarHora = (hora) => {
    return hora ? hora.substring(0, 5) : '';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'planejada': { class: 'status-planejada', text: 'Planejada', icon: FiClock },
      'realizada': { class: 'status-realizada', text: 'Realizada', icon: FiCheckCircle },
      'cancelada': { class: 'status-cancelada', text: 'Cancelada', icon: FiXCircle }
    };
    
    const statusInfo = statusMap[status] || { class: 'status-default', text: status, icon: FiClock };
    const IconComponent = statusInfo.icon;
    
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        <IconComponent size={14} />
        {statusInfo.text}
      </span>
    );
  };

  const calcularEstatisticasPresenca = () => {
    const total = participantes.length;
    const presentes = participantes.filter(p => p.presente).length;
    const ausentes = total - presentes;
    const percentualPresenca = total > 0 ? Math.round((presentes / total) * 100) : 0;
    
    return { total, presentes, ausentes, percentualPresenca };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados da visita...</p>
      </div>
    );
  }

  if (!visita) {
    return (
      <div className="error-container">
        <h2>Visita não encontrada</h2>
        <p>A visita solicitada não foi encontrada.</p>
        <Link to="/visitas" className="btn btn-primary">
          <FiArrowLeft /> Voltar para Visitas
        </Link>
      </div>
    );
  }

  const stats = calcularEstatisticasPresenca();

  return (
    <div className="visita-detalhes-container">
      {/* Header */}
      <div className="detalhes-header">
        <div className="header-navigation">
          <button 
            onClick={() => navigate('/visitas')} 
            className="btn btn-secondary"
          >
            <FiArrowLeft /> Voltar
          </button>
        </div>
        
        <div className="header-content">
          <div className="header-info">
            <div className="visita-tipo">
              {tiposVisita[visita.tipo_visita] || visita.tipo_visita}
            </div>
            <h1>{visita.titulo}</h1>
            {getStatusBadge(visita.status)}
          </div>
          
          <div className="header-actions">
            <Link 
              to={`/visitas/editar/${visita.id}`} 
              className="btn btn-primary"
            >
              <FiEdit /> Editar
            </Link>
            
            <button 
              onClick={excluirVisita}
              className="btn btn-danger"
            >
              <FiTrash2 /> Excluir
            </button>
          </div>
        </div>
      </div>

      <div className="detalhes-content">
        {/* Informações Básicas */}
        <div className="info-section">
          <h2>Informações da Visita</h2>
          
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">
                <FiCalendar />
              </div>
              <div className="info-content">
                <label>Data</label>
                <span>{formatarData(visita.data_visita)}</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FiClock />
              </div>
              <div className="info-content">
                <label>Horário</label>
                <span>
                  {formatarHora(visita.hora_inicio)}
                  {visita.hora_fim && ` - ${formatarHora(visita.hora_fim)}`}
                </span>
              </div>
            </div>
            
            {visita.local_visita && (
              <div className="info-item">
                <div className="info-icon">
                  <FiMapPin />
                </div>
                <div className="info-content">
                  <label>Local</label>
                  <span>{visita.local_visita}</span>
                </div>
              </div>
            )}
            
            {visita.responsavel_nome && (
              <div className="info-item">
                <div className="info-icon">
                  <FiUser />
                </div>
                <div className="info-content">
                  <label>Responsável</label>
                  <span>{visita.responsavel_nome}</span>
                </div>
              </div>
            )}
          </div>
          
          {visita.endereco && (
            <div className="endereco-completo">
              <label>Endereço Completo</label>
              <p>{visita.endereco}</p>
            </div>
          )}
          
          {visita.observacoes && (
            <div className="observacoes">
              <label><FiFileText /> Observações</label>
              <p>{visita.observacoes}</p>
            </div>
          )}
        </div>

        {/* Estatísticas de Presença */}
        {participantes.length > 0 && (
          <div className="stats-section">
            <h2>Estatísticas de Presença</h2>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon total">
                  <FiUsers />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stats.total}</span>
                  <span className="stat-label">Total de Participantes</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon presentes">
                  <FiCheckCircle />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stats.presentes}</span>
                  <span className="stat-label">Presentes</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon ausentes">
                  <FiXCircle />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stats.ausentes}</span>
                  <span className="stat-label">Ausentes</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon percentual">
                  <span>{stats.percentualPresenca}%</span>
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stats.percentualPresenca}%</span>
                  <span className="stat-label">Taxa de Presença</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Participantes */}
        <div className="participantes-section">
          <div className="section-header">
            <h2>Participantes ({participantes.length})</h2>
            <Link 
              to={`/visitas/editar/${visita.id}`} 
              className="btn btn-primary btn-sm"
            >
              <FiPlus /> Gerenciar Participantes
            </Link>
          </div>
          
          {participantes.length === 0 ? (
            <div className="empty-participants">
              <FiUsers size={48} />
              <h3>Nenhum participante cadastrado</h3>
              <p>Esta visita ainda não possui participantes.</p>
              <Link 
                to={`/visitas/editar/${visita.id}`} 
                className="btn btn-primary"
              >
                <FiPlus /> Adicionar Participantes
              </Link>
            </div>
          ) : (
            <div className="participantes-list">
              {participantes.map(participante => (
                <div 
                  key={participante.membro_id} 
                  className={`participante-item ${participante.presente ? 'presente' : 'ausente'}`}
                >
                  <div className="participante-info">
                    <div className="participante-avatar">
                      {participante.nome.charAt(0).toUpperCase()}
                    </div>
                    
                    <div className="participante-dados">
                      <div className="participante-nome">{participante.nome}</div>
                      <div className="participante-categoria">{participante.categoria}</div>
                    </div>
                  </div>
                  
                  <div className="participante-presenca">
                    <button
                      onClick={() => togglePresenca(participante.membro_id, participante.presente)}
                      className={`presenca-toggle ${participante.presente ? 'presente' : 'ausente'}`}
                      disabled={atualizandoPresenca}
                      title={participante.presente ? 'Marcar como ausente' : 'Marcar como presente'}
                    >
                      {participante.presente ? (
                        <>
                          <FiCheckCircle />
                          Presente
                        </>
                      ) : (
                        <>
                          <FiXCircle />
                          Ausente
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitaDetalhes;